import React from "react";
import { motion } from "framer-motion";
import FloatingImage from "./FloatingImage";
import HydrogelHero from "./HydrogelHero";

const HeroPanel = ({
    title,
    subtitle,
    image,
    price,
    devices = [],
    features = [],
    examples = [],
    delay = 0,
}) => {
    const hasImage = image && image.trim().length > 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex flex-col h-full rounded-[2rem] border border-[#d4af37]/10 bg-gradient-to-br from-white via-[#FAFAFC] to-[#F3F0FA] p-5 md:p-7 overflow-hidden shadow-[0_20px_60px_-25px_rgba(139,92,246,0.12)]"
        >
            {/* Subtle gold-purple background glow */}
            <div
                className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-30 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle, rgba(212,175,55,0.18) 0%, transparent 70%)",
                }}
            />
            <div
                className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full opacity-30 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle, rgba(139,92,246,0.16) 0%, transparent 70%)",
                }}
            />

            {/* Text */}
            <div className="relative z-10 mb-4">
                <h2
                    dir="auto"
                    className="font-display text-2xl md:text-3xl lg:text-[32px] font-medium leading-[1.15] tracking-tight text-neutral-900"
                >
                    {title}
                </h2>
                <p
                    dir="auto"
                    className="mt-2 text-[14px] md:text-[15px] leading-relaxed text-neutral-500"
                >
                    {subtitle}
                </p>
            </div>

            {/* Visual */}
            <div className="relative z-10 flex-1 min-h-[220px] md:min-h-[280px] flex items-center justify-center my-3">
                {hasImage ? (
                    <FloatingImage
                        src={image}
                        alt={title}
                        delay={delay + 0.15}
                        className="max-w-[280px] md:max-w-[340px]"
                    />
                ) : (
                    <div className="w-full max-w-[300px]">
                        <HydrogelHero />
                    </div>
                )}
            </div>

            {/* Devices / features */}
            <div className="relative z-10 mt-auto space-y-3">
                {devices.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {devices.map((device) => (
                            <span
                                key={device}
                                dir="auto"
                                className="inline-flex items-center rounded-full bg-[#d4af37]/10 text-[#7c6200] px-2.5 py-1 text-[11px] font-medium"
                            >
                                {device}
                            </span>
                        ))}
                    </div>
                )}

                {features.length > 0 && (
                    <ul className="space-y-1.5">
                        {features.map((feature) => (
                            <li
                                key={feature}
                                dir="auto"
                                className="flex items-start gap-2 text-[13px] text-neutral-700"
                            >
                                <span className="mt-1.5 w-1 h-1 rounded-full bg-[#8b5cf6] shrink-0" />
                                {feature}
                            </li>
                        ))}
                    </ul>
                )}

                {price && (
                    <div className="pt-1">
                        <span
                            dir="auto"
                            className="inline-flex items-center rounded-full bg-[#8b5cf6]/10 text-[#7c3aed] px-3 py-1 text-[13px] font-bold tabular-nums"
                        >
                            {price}
                        </span>
                    </div>
                )}

                {/* Examples gallery */}
                {examples.length > 0 && (
                    <div className="pt-2">
                        <p className="text-[11px] uppercase tracking-wider text-neutral-400 mb-2">
                            Exemples
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {examples.map((url, i) => (
                                <motion.img
                                    key={url + i}
                                    src={url}
                                    alt="exemple"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: delay + 0.3 + i * 0.08 }}
                                    className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-xl border border-white/60 shadow-sm bg-white"
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default HeroPanel;
